import { observer } from 'mobx-react';
import { useCallback, useMemo } from 'react';
import DetailsLogs from '../DetailsLogs/DetailsLogs';
import ExportPdfButton from './components/ExportPdfButton/ExportPdfButton';
import { getOrderDeliveryTypeTitle } from 'src/constants/deliveryTypes';
import { ORDER_STATUS } from 'src/constants/order';
import { USER_PERMISSIONS } from 'src/constants/user';
import { localeStore } from 'src/mobx/localesStore';
import { TOrder } from 'src/types/orders';
import { getTranslate } from 'src/utils/helpers';
import AggregatorManager from 'src/view/components/AggregatorManager/AggregatorManager';
import OrderStatusPicker from 'src/view/components/Controls/Pickers/OrderStatusPicker/OrderStatusPicker';
import Permission from 'src/view/components/Permission/Permission';
import OrderBlockHeader from 'src/view/screens/OrderScreen/components/OrderBlockHeader/OrderBlockHeader';
import OrderBlockItem from 'src/view/screens/OrderScreen/components/OrderBlockItem/OrderBlockItem';
import { useOrderStoreContext } from '~/view/screens/OrderScreen/context';
import './style.scss';
import { statusManagementAvailable } from '~/view/components/AggregatorManager/helpers/AggregatorManagerHelpers';

const DetailsOrder: React.FC = observer((): JSX.Element | null => {
  const { store: orderStore } = useOrderStoreContext();
  const orderStatusPickerDisabled = useMemo((): boolean => {
    return Boolean(orderStore.order?.aggregator_key) && !statusManagementAvailable(orderStore.order);
  }, [orderStore.order]);

  const changeOrderStatus = useCallback((orderId: TOrder['id'], newStatus: TOrder['order_status_code']) => {
    orderStore.changeStatus(orderId, newStatus);
  }, []);

  if (!orderStore.order) return null;

  return (
    <div className="details-order mr-6">
      {/* header */}
      <OrderBlockHeader title="Order" />
      <ul className="d-flex flex-column">
        {/* order id */}
        <OrderBlockItem title="txt_order_id" titleClassName="details-order__item-title" data={orderStore.order.order_key} />
        {/* created by */}
        <OrderBlockItem title="created_by" titleClassName="details-order__item-title" data={orderStore.order.created_by_name} />
        {/* delivery type */}
        <OrderBlockItem
          title="txt_delivery_type"
          titleClassName="details-order__item-title"
          data={localeStore.t(getOrderDeliveryTypeTitle(orderStore.order))}
        />
        {/* branch */}
        <OrderBlockItem title="txt_waitlist_branch" titleClassName="details-order__item-title" data={getTranslate(orderStore.order.branch?.name)} />
        {/* logs */}
        <DetailsLogs orderId={orderStore.order?.id} aggregatorKey={orderStore.order.aggregator_key} />
        {/* change status picker */}
        <Permission permissions={USER_PERMISSIONS.ORDERS_MANAGMENT}>
          {!orderStore.order ||
          [ORDER_STATUS.COMPLETED, ORDER_STATUS.CANCELED, ORDER_STATUS.WAITING_FOR_PAYMENT, ORDER_STATUS.ON_HOLD].includes(
            orderStore.order.order_status_code
          ) ? null : (
            <OrderBlockItem title="txt_delivery_status_h" titleClassName="details-order__item-title">
              <OrderStatusPicker
                order={orderStore.order}
                onStatusChange={changeOrderStatus}
                disabled={orderStatusPickerDisabled}
                loading={orderStore.loading}
              />
            </OrderBlockItem>
          )}
        </Permission>
        {/* export button */}
        <AggregatorManager aggregator={orderStore.order?.aggregator_key} sectionName="export">
          <ExportPdfButton />
        </AggregatorManager>
      </ul>
    </div>
  );
});

export default DetailsOrder;
